import React, { useEffect, useMemo, useState } from "react";
import API from "../../services/api";
import {
    confirmationMatches,
    filterArchivedTags,
    formatArchivedAt,
    sortArchivedTags,
} from "./archivedTags";
import { intervalLabel } from "./tagCreation";

const ArchivedTags = ({ servers, serverNameById, onChanged }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [serverFilter, setServerFilter] = useState("all");
    const [sortDirection, setSortDirection] = useState("desc");
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [typedName, setTypedName] = useState("");
    const [typedId, setTypedId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const [notice, setNotice] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await API.get("/tags/archived/");
            const nextRows = Array.isArray(response.data) ? response.data : [];
            setRows(nextRows);
            setSelectedId((current) => (nextRows.some((row) => row.id === current) ? current : null));
        } catch (loadError) {
            setError(loadError?.response?.data?.detail || "Archived tags are not available for this session yet.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (!selectedId) {
            setDetail(null);
            return undefined;
        }
        let active = true;
        setDetailError("");
        API.get(`/tags/${selectedId}/`).then((response) => {
            if (active) {
                setDetail(response.data);
                setJob(response.data.deletion_job || null);
            }
        }).catch((loadError) => {
            if (active) {
                setDetail(null);
                setDetailError(loadError?.response?.data?.detail || "Tag details are not available.");
            }
        });
        return () => {
            active = false;
        };
    }, [selectedId]);

    useEffect(() => {
        if (!job || (job.status !== "pending" && job.status !== "running")) {
            return undefined;
        }
        const timer = window.setInterval(async () => {
            try {
                const response = await API.get(`/tag-deletion-jobs/${job.id}/`);
                setJob(response.data);
                if (response.data.status === "completed") {
                    setNotice(`${response.data.tag_name} and its history were deleted.`);
                    setSelectedId(null);
                    setDialogOpen(false);
                    onChanged?.();
                    await load();
                }
                if (response.data.status === "failed") {
                    setNotice("");
                    setError(response.data.error || "The deletion job failed.");
                }
            } catch (pollError) {
                setError(pollError?.response?.data?.detail || "Deletion progress could not be read.");
            }
        }, 2000);
        return () => window.clearInterval(timer);
    }, [job, onChanged]);

    const visible = useMemo(
        () => sortArchivedTags(filterArchivedTags(rows, { query, serverId: serverFilter }), sortDirection),
        [rows, query, serverFilter, sortDirection],
    );

    const restore = async () => {
        if (!detail || submitting) return;
        setSubmitting(true);
        setError("");
        setNotice("");
        try {
            await API.post(`/tags/${detail.id}/restore/`);
            setNotice(`${detail.name} is active again.`);
            setSelectedId(null);
            onChanged?.();
            await load();
        } catch (restoreError) {
            setError(restoreError?.response?.data?.detail || "The tag could not be restored.");
        } finally {
            setSubmitting(false);
        }
    };

    const queueDeletion = async () => {
        if (!detail || submitting || !confirmationMatches(detail, typedName, typedId)) return;
        setSubmitting(true);
        setError("");
        setNotice("");
        try {
            const response = await API.post(`/tags/${detail.id}/permanent-delete/`, {
                confirm_name: typedName,
                confirm_id: Number(typedId),
            });
            setJob(response.data);
            setDialogOpen(false);
            setTypedName("");
            setTypedId("");
            setNotice("Deletion is queued. History stays until the backup is verified.");
        } catch (deleteError) {
            const data = deleteError?.response?.data || {};
            const references = Array.isArray(data.references)
                ? data.references.map((item) => item.detail).filter(Boolean).join(" ")
                : "";
            setError([data.detail, references].filter(Boolean).join(" ") || "The tag could not be queued for deletion.");
        } finally {
            setSubmitting(false);
        }
    };

    const matched = confirmationMatches(detail, typedName, typedId);
    const jobStatus = job?.status || detail?.deletion_job?.status;
    const restoreBlocked = Boolean(detail?.restore_blocked) || jobStatus === "pending" || jobStatus === "running";

    return (
        <div className="tag-page__archived-page">
            <div className="tag-page__registry-tools">
                <div className="tag-page__field">
                    <label htmlFor="archived-search">Search</label>
                    <input
                        id="archived-search"
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Name or Node ID"
                    />
                </div>
                <div className="tag-page__field">
                    <label htmlFor="archived-server">OPC Server</label>
                    <select id="archived-server" value={serverFilter} onChange={(event) => setServerFilter(event.target.value)}>
                        <option value="all">All servers</option>
                        {(servers || []).map((server) => (
                            <option key={server.id} value={String(server.id)}>{server.name}</option>
                        ))}
                    </select>
                </div>
                <div className="tag-page__field">
                    <label htmlFor="archived-sort">Archived at</label>
                    <select id="archived-sort" value={sortDirection} onChange={(event) => setSortDirection(event.target.value)}>
                        <option value="desc">Newest first</option>
                        <option value="asc">Oldest first</option>
                    </select>
                </div>
                <button type="button" className="tag-page__button" onClick={load}>Refresh</button>
            </div>
            {error ? <p className="tag-page__banner is-error" role="alert">{error}</p> : null}
            {notice ? <p className="tag-page__banner is-success" role="status">{notice}</p> : null}
            {loading ? <p className="tag-page__empty">Loading archived tags…</p> : null}
            {!loading && visible.length === 0 ? (
                <p className="tag-page__empty">{rows.length === 0 ? "No archived tags." : "No archived tags match this search."}</p>
            ) : null}
            {!loading && visible.length > 0 ? (
                <div className="tag-page__registry-scroll">
                    <table className="tag-page__registry-table">
                        <thead>
                            <tr>
                                <th scope="col">Tag ID</th>
                                <th scope="col">Tag name</th>
                                <th scope="col">Type</th>
                                <th scope="col">Signal</th>
                                <th scope="col">Node ID</th>
                                <th scope="col">OPC Server</th>
                                <th scope="col">Archived at</th>
                                <th scope="col"><span className="tag-page__sr">Details</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((row) => {
                                const serverName = serverNameById?.[String(row.server)] || row.server_name || row.server;
                                const kind = row.is_calculated ? "CALCULATED" : "PHYSICAL";
                                return (
                                    <tr key={row.id} className={row.id === selectedId ? "is-selected" : undefined}>
                                        <td data-label="Tag ID" className="tag-page__mono">{row.id}</td>
                                        <td data-label="Tag name"><span className="tag-page__strong">{row.name}</span></td>
                                        <td data-label="Type">{kind}</td>
                                        <td data-label="Signal">{row.signal_type || "—"}</td>
                                        <td data-label="Node ID" className="tag-page__mono">{row.node_id}</td>
                                        <td data-label="OPC Server">{serverName}</td>
                                        <td data-label="Archived at">{formatArchivedAt(row.archived_at)}</td>
                                        <td data-label="Details">
                                            <button type="button" className="tag-page__button" onClick={() => {
                                                setSelectedId(row.id);
                                                setDialogOpen(false);
                                                setError("");
                                            }}>
                                                Details
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}
            {selectedId ? (
                <section className="tag-page__card tag-page__archived-detail" aria-labelledby="archived-detail-heading">
                    <div className="tag-page__manage-head">
                        <div>
                            <h2 id="archived-detail-heading">Archived tag details</h2>
                            <p>Archived means the tag is out of service. It is separate from the OPC connection status.</p>
                        </div>
                        <button type="button" className="tag-page__button" onClick={() => { setSelectedId(null); setDialogOpen(false); }}>
                            Close
                        </button>
                    </div>
                    {detailError ? <p className="tag-page__banner is-error" role="alert">{detailError}</p> : null}
                    {!detail ? <p className="tag-page__hint">Loading details…</p> : (
                        <>
                            <dl className="tag-page__identity">
                                <div><dt>Tag ID</dt><dd className="tag-page__mono">{detail.id}</dd></div>
                                <div><dt>Tag name</dt><dd>{detail.name}</dd></div>
                                <div><dt>Description</dt><dd>{detail.description || "—"}</dd></div>
                                <div><dt>Node ID</dt><dd className="tag-page__mono">{detail.node_id}</dd></div>
                                <div><dt>OPC Server</dt><dd>{detail.server_name || serverNameById?.[String(detail.server)] || detail.server}</dd></div>
                                <div><dt>Archived at</dt><dd>{formatArchivedAt(detail.archived_at)}</dd></div>
                                <div><dt>Lifecycle</dt><dd><span className="tag-page__lifecycle tag-page__lifecycle--ARCHIVED">ARCHIVED</span></dd></div>
                                <div><dt>Signal type</dt><dd>{detail.signal_type === "DIGITAL" ? "Digital" : detail.signal_type === "ANALOG" ? "Analog" : detail.signal_type === "STRING" ? "String" : "—"}</dd></div>
                                <div><dt>OPC data type</dt><dd className="tag-page__mono">{detail.opc_data_type || "—"}</dd></div>
                                <div><dt>Tag type</dt><dd>{detail.is_calculated ? "Calculated" : "Physical"}</dd></div>
                                <div><dt>Formula</dt><dd className="tag-page__mono">{detail.formula || "—"}</dd></div>
                                <div><dt>Access mode</dt><dd>{detail.access_mode || "—"}</dd></div>
                                <div><dt>Recording mode</dt><dd>{detail.recording_mode || detail.effective?.recording_mode || "ALWAYS"}</dd></div>
                                <div><dt>On Change Interval</dt><dd>{intervalLabel(detail.on_change_interval_seconds ?? ((detail.on_change_interval_minutes ?? 5) * 60))}</dd></div>
                                <div><dt>Decimal places</dt><dd>{detail.decimal_places ?? detail.effective?.decimal_places ?? "—"}</dd></div>
                                <div><dt>Tag group</dt><dd>{detail.group_name || "No group"}</dd></div>
                                <div><dt>Inherited access</dt><dd>{detail.effective?.access_mode || "—"}</dd></div>
                                <div><dt>Inherited recording</dt><dd>{detail.effective?.recording_mode || "ALWAYS"}</dd></div>
                                <div><dt>Last good value</dt><dd>{detail.last_observed_value ?? detail.last_value ?? "—"}</dd></div>
                                <div><dt>Last successful read</dt><dd>{formatArchivedAt(detail.last_successful_read_at)}</dd></div>
                                <div><dt>Last recorded connection</dt><dd>{detail.stored_connection_status || "—"}</dd></div>
                                <div><dt>History</dt><dd>{detail.has_history ? "History rows are still stored." : "No history rows are stored."}</dd></div>
                            </dl>
                            {job ? (
                                <p className={`tag-page__banner ${job.status === "failed" ? "is-error" : job.status === "completed" ? "is-success" : "is-pending"}`} role="status">
                                    Deletion job {job.id} is {job.status}.
                                    {job.backup_row_count != null ? ` Backup rows ${job.backup_row_count}.` : ""}
                                    {job.deleted_rows ? ` Deleted rows ${job.deleted_rows}.` : ""}
                                    {job.error ? ` ${job.error}` : ""}
                                    {job.detail ? ` ${job.detail}` : ""}
                                </p>
                            ) : null}
                            <div className="tag-page__actions">
                                <button type="button" className="tag-page__button" onClick={restore} disabled={submitting || restoreBlocked}>
                                    Restore Tag
                                </button>
                                {detail.can_permanently_delete ? (
                                    <button
                                        type="button"
                                        className="tag-page__button tag-page__button--danger"
                                        onClick={() => {
                                            setDialogOpen(true);
                                            setTypedName("");
                                            setTypedId("");
                                            setError("");
                                        }}
                                        disabled={submitting}
                                    >
                                        Permanently Delete Tag & History
                                    </button>
                                ) : null}
                            </div>
                            {dialogOpen ? (
                                <div className="tag-page__dialog" role="dialog" aria-modal="true" aria-labelledby="permanent-delete-title">
                                    <h3 id="permanent-delete-title">Permanently Delete Tag & History</h3>
                                    <p>
                                        This permanently removes {detail.name}, tag ID {detail.id}, on {detail.server_name || "this OPC server"}.
                                        Every TagValue row for this tag is deleted after a verified backup of those rows. Other tags are left unchanged.
                                    </p>
                                    <label className="tag-page__field">
                                        <span>Type the exact tag name</span>
                                        <input value={typedName} onChange={(event) => setTypedName(event.target.value)} autoComplete="off" />
                                    </label>
                                    <label className="tag-page__field">
                                        <span>Type the exact tag ID</span>
                                        <input value={typedId} onChange={(event) => setTypedId(event.target.value)} autoComplete="off" inputMode="numeric" />
                                    </label>
                                    <div className="tag-page__actions">
                                        <button type="button" className="tag-page__button" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancel</button>
                                        <button type="button" className="tag-page__button tag-page__button--danger" onClick={queueDeletion} disabled={!matched || submitting}>
                                            {submitting ? "Queuing…" : "Delete"}
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    )}
                </section>
            ) : null}
        </div>
    );
};

export default ArchivedTags;
